import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronRight, Search } from 'lucide-react';
import { Input } from './ui/input';
import { getCached, setCached, CACHE_TTL } from '../utils/cacheUtils';
import { PAY_TYPE_COLORS, DEFAULT_MULTIPLIERS, getPayTypeColor } from '../utils/payTypeColors';
import { AppContext } from '../App';

const JobTitleBreakdown = ({ API, filters, formatCurrency, formatHours, onRowClick }) => {
  const { premiumPayTypes } = React.useContext(AppContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('cost');
  const [sortOrder, setSortOrder] = useState('desc');

  // Create a stable string key for all filters to trigger re-fetches
  const filterKey = JSON.stringify({
    start_date: filters?.start_date || null,
    end_date: filters?.end_date || null,
    location: filters?.location || null,
    department: filters?.department || null,
    manager: filters?.manager || null,
    pay_types: filters?.pay_types || []
  });

  useEffect(() => {
    const fetchData = async () => {
      if (process.env.NODE_ENV === 'development') {
      }
      
      const params = {
        group_by: 'job_title',
        start_date: filters?.start_date || undefined,
        end_date: filters?.end_date || undefined,
        location: filters?.location || undefined,
        department: filters?.department || undefined,
        manager: filters?.manager || undefined,
        pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
      };
      
      // Clean params - remove undefined values for consistent cache keys
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== undefined)
      );
      
      if (process.env.NODE_ENV === 'development') {
      }
      
      // DISABLED cache to ensure fresh data with filter changes
      // const cachedData = getCached('jobtitle_breakdown', cleanParams);
      // if (cachedData) {
      //   setData(cachedData);
      //   setLoading(false);
      //   return;
      // }
      
      setLoading(true);
      try {
        const response = await axios.post(`${API}/overtime/analysis`, params);
        if (process.env.NODE_ENV === 'development') {
        }
        setData(response.data);
        
        // Still cache for future use (but check is disabled)
        setCached('jobtitle_breakdown', cleanParams, response.data, CACHE_TTL.LONG);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching job title data:', error);
        }
        toast.error('Failed to load job title breakdown');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API, filterKey]); // Use filterKey as single dependency

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedAndFilteredData = () => {
    if (!data?.records) return [];
    
    let filtered = data.records;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(r => 
        r.dimension.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort
    return [...filtered].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 bg-slate-200 rounded"></div>
        <div className="h-[400px] bg-slate-100 rounded-lg"></div>
        <div className="h-[300px] bg-slate-100 rounded-lg"></div>
      </div>
    );
  }

  const sortedData = getSortedAndFilteredData();
  const top10 = sortedData.slice(0, 10);

  // Custom tooltip showing all pay type breakdowns
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      const breakdown = (d.premium_breakdown || []).filter(it => it.hours > 0);
      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-[#1e3a5f] mb-2">{d.dimension}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Total Premium:</span>
              <span className="font-medium">{formatHours(d.total_premium_hours || 0)} hrs</span>
            </div>
            <div className="border-t border-slate-100 my-1 pt-1">
              {breakdown.map(item => (
                <div key={item.pay_type} className="flex justify-between gap-4">
                  <span style={{ color: getPayTypeColor(item.pay_type) }}>
                    {item.pay_type} ({item.multiplier}x):
                  </span>
                  <span className="font-medium">{formatHours(item.hours)} hrs</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 mt-1 pt-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Premium Cost:</span>
                <span className="font-medium text-orange-600">{formatCurrency(d.cost || 0)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Employees:</span>
                <span className="font-medium">{d.employee_count}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const SortHeader = ({ column, label }) => (
    <th 
      className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:text-foreground"
      onClick={() => handleSort(column)}
    >
      <span className="flex items-center justify-end gap-1">
        {label}
        {sortBy === column && (
          <span className="text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Top 10 Chart - Horizontal Stacked by Pay Type */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="kpi-card"
      >
        <h3 className="text-lg font-semibold text-[#1e3a5f] mb-1">Top 10 Job Titles by Premium Hours</h3>
        <p className="text-sm text-muted-foreground mb-4">Breakdown by pay type - Click to view employees</p>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={top10} 
            layout="vertical"
            onClick={(e) => {
              if (e?.activePayload?.[0]?.payload?.dimension) {
                onRowClick && onRowClick(e.activePayload[0].payload.dimension, null, 'job_title');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis type="number" tickFormatter={(v) => formatHours(v)} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="dimension" width={180} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {/* Dynamic stacked bars from AppPayType.PremiumFlag */}
            {premiumPayTypes.map((pt, idx) => (
              <Bar
                key={pt.pay_type}
                dataKey={(record) => {
                  const item = record?.premium_breakdown?.find(b => b.pay_type === pt.pay_type);
                  return item?.hours || 0;
                }}
                stackId="premium"
                fill={getPayTypeColor(pt.pay_type)}
                name={`${pt.pay_type} (${pt.multiplier}x)`}
                radius={idx === premiumPayTypes.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Detail Table with Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="kpi-card"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[#1e3a5f]">All Job Titles</h3>
            <p className="text-sm text-muted-foreground">{sortedData.length} job titles found</p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Search job titles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="job-title-search"
            />
          </div>
        </div>
        
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Job Title</th>
                <SortHeader column="total_premium_hours" label="Premium Hrs" />
                {/* Dynamic per-pay-type columns */}
                {premiumPayTypes.map(pt => (
                  <th
                    key={`hdr-${pt.pay_type}`}
                    className="text-right py-3 px-4 text-xs font-semibold uppercase"
                    style={{ color: getPayTypeColor(pt.pay_type) }}
                  >
                    {pt.pay_type}
                  </th>
                ))}
                <SortHeader column="cost" label="Premium Cost" />
                <SortHeader column="employee_count" label="Employees" />
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sortedData.slice(0, 50).map((record, idx) => (
                <tr 
                  key={record.dimension || record.job_title} 
                  className="border-b border-border hover:bg-slate-50 transition-colors cursor-pointer group"
                  onClick={() => onRowClick && onRowClick(record.dimension, null, 'job_title')}
                  data-testid={`job-title-row-${idx}`}
                >
                  <td className="py-3 px-4 font-medium text-foreground">{record.dimension}</td>
                  <td className="py-3 px-4 text-right tabular-nums font-medium text-orange-600">{formatHours(record.total_premium_hours || 0)}</td>
                  {premiumPayTypes.map(pt => {
                    const item = (record.premium_breakdown || []).find(b => b.pay_type === pt.pay_type);
                    return (
                      <td
                        key={`cell-${record.dimension}-${pt.pay_type}`}
                        className="py-3 px-4 text-right tabular-nums"
                        style={{ color: getPayTypeColor(pt.pay_type) }}
                      >
                        {formatHours(item?.hours || 0)}
                      </td>
                    );
                  })}
                  <td className="py-3 px-4 text-right tabular-nums">{formatCurrency(record.cost || 0)}</td>
                  <td className="py-3 px-4 text-right tabular-nums">{record.employee_count}</td>
                  <td className="py-3 px-4">
                    <ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedData.length > 50 && (
          <p className="text-sm text-muted-foreground text-center mt-4">
            Showing top 50 of {sortedData.length} job titles. Use search to find specific roles.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default JobTitleBreakdown;
